import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  Button,
  ButtonBase,
  Chip,
  ChipProps,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  ICourseStudentForAdmin,
  deleteCourseStudent,
  getCourseStudentsAsAdmin,
  getCourseStudentsState,
  resetAdminCourseStudentsData,
  updateCourseStudent,
} from '../../../../../../../store/V2/CourseStudentsSlice';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { getCoursesState } from '../../../../../../../store/V2/CoursesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/UserSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { checkUserPermission } from '../../../../../../../helper';
import { TabPanel } from '..';
import AddCourseStudentModal from './modals/AddCourseStudentModal';
import BasicLoader from '../../../../../../../components/Loaders/BasicLoader';

type TEditCourseStudentModalData = {
  open: boolean;
  courseStudent?: ICourseStudentForAdmin;
};

interface StudentsTabProps {
  selectedTabValue: number;
  selectedTabIndex: number;
}

export const TableHeaderRowCell = styled(TableCell)({
  '&': {
    fontWeight: 600,
  },
});

const TableBodyRowCell = ({ children }) => {
  return (
    <TableCell component="th" scope="row">
      {children}
    </TableCell>
  );
};

const getFormatedStatus = (status: ICourseStudentForAdmin['status']) => {
  let newStatus = '';

  switch (status) {
    case 'ENABLE':
      newStatus = 'Enabled';
      break;
    case 'DISABLE':
      newStatus = 'Disabled';
      break;

    default:
      newStatus = '';
      break;
  }

  return newStatus;
};

const StudentsTab: React.FC<StudentsTabProps> = ({
  selectedTabValue,
  selectedTabIndex,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const authState = useAppSelector(getAuthState);
  const coursesState = useAppSelector(getCoursesState);
  const courseStudentsState = useAppSelector(getCourseStudentsState);

  // State
  const [isAddCourseStudentModalOpen, setAddCourseStudentModalOpen] = useState(
    false
  );

  const [
    editCourseStudentModalData,
    setEditCourseStudentModalData,
  ] = useState<TEditCourseStudentModalData>({
    open: false,
  });

  const courseData = coursesState.adminCourse;
  const { adminCourseStudentsData } = courseStudentsState;

  useEffect(() => {
    if (courseData) {
      dispatch(
        getCourseStudentsAsAdmin({
          course: courseData._id,
          limit: 200,
          offset: 0,
        })
      );
    }

    return () => {
      dispatch(resetAdminCourseStudentsData());
    };
  }, []);

  const toggleAddCourseStudentModal = () => {
    setAddCourseStudentModalOpen(!isAddCourseStudentModalOpen);
  };

  const openCourseStudentEditModal = (
    courseStudent: ICourseStudentForAdmin
  ) => {
    setEditCourseStudentModalData({
      courseStudent,
      open: true,
    });
  };

  const closeCourseStudentEditModal = () => {
    setEditCourseStudentModalData({
      open: false,
    });
  };

  return (
    <TabPanel value={selectedTabValue} index={selectedTabIndex}>
      <div style={{ marginBottom: '30px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Students</Typography>
          {checkUserPermission(authState.user, {
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
          }) ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={toggleAddCourseStudentModal}
            >
              Add
            </Button>
          ) : null}
        </Stack>
      </div>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderRowCell>Name</TableHeaderRowCell>
                    <TableHeaderRowCell>Start Date</TableHeaderRowCell>
                    <TableHeaderRowCell>End Date</TableHeaderRowCell>
                    <TableHeaderRowCell>Status</TableHeaderRowCell>
                    {checkUserPermission(authState.user, {
                      adminCourse: [
                        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                      ],
                    }) ? (
                      <TableHeaderRowCell>Actions</TableHeaderRowCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminCourseStudentsData &&
                  adminCourseStudentsData.list &&
                  adminCourseStudentsData.list.length
                    ? adminCourseStudentsData.list.map((item) => {
                        const _student = item.student;

                        let _name = '';

                        if (_student) {
                          _name = _student.firstname;

                          if (_student.lastname) {
                            _name += ` ${_student.lastname}`;
                          }
                        }

                        const _statusChipProps: ChipProps = {
                          size: 'small',
                          label: getFormatedStatus(item.status),
                          color: 'success',
                        };

                        if (item.status !== 'ENABLE') {
                          _statusChipProps.color = 'error';
                        }

                        return (
                          <TableRow
                            key={`td-${item._id}`}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                          >
                            <TableBodyRowCell>{_name}</TableBodyRowCell>
                            <TableBodyRowCell>
                              {item.startDate
                                ? moment(item.startDate).format(
                                    'ddd MMM DD YYYY hh:mm A'
                                  )
                                : '-'}
                            </TableBodyRowCell>
                            <TableBodyRowCell>
                              {item.endDate
                                ? moment(item.endDate).format(
                                    'ddd MMM DD YYYY hh:mm A'
                                  )
                                : '-'}
                            </TableBodyRowCell>
                            <TableBodyRowCell>
                              <Chip {..._statusChipProps} />
                            </TableBodyRowCell>
                            {checkUserPermission(authState.user, {
                              adminCourse: [
                                USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                              ],
                            }) ? (
                              <TableBodyRowCell>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={2}
                                >
                                  <ButtonBase
                                    onClick={() => {
                                      confirm({
                                        title: 'Are you sure?',
                                        description: `Do you want to delete ${_name}?`,
                                      }).then(() => {
                                        dispatch(deleteCourseStudent(item._id));
                                      });
                                    }}
                                    sx={{
                                      backgroundColor: '#F1F5F9',
                                      padding: '5px',
                                      borderRadius: '5px',
                                    }}
                                  >
                                    <DeleteIcon color="error" />
                                  </ButtonBase>
                                  <ButtonBase
                                    onClick={() =>
                                      openCourseStudentEditModal(item)
                                    }
                                    sx={{
                                      backgroundColor: '#F1F5F9',
                                      padding: '5px',
                                      borderRadius: '5px',
                                    }}
                                  >
                                    <EditIcon color="inherit" />
                                  </ButtonBase>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      confirm({
                                        title: 'Are you sure?',
                                        description: `Do you want to update ${_name}'s status?`,
                                      }).then(() => {
                                        dispatch(
                                          updateCourseStudent({
                                            id: item._id,
                                            status:
                                              item.status === 'ENABLE'
                                                ? 'DISABLE'
                                                : 'ENABLE',
                                          })
                                        );
                                      });
                                    }}
                                    color={
                                      item.status === 'ENABLE'
                                        ? 'error'
                                        : 'info'
                                    }
                                  >
                                    {item.status === 'ENABLE'
                                      ? 'Disable'
                                      : 'Enable'}
                                  </Button>
                                </Stack>
                              </TableBodyRowCell>
                            ) : null}
                          </TableRow>
                        );
                      })
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {courseStudentsState.status === 'loading' &&
          !adminCourseStudentsData ? (
            <Grid item xs={12}>
              <BasicLoader />
            </Grid>
          ) : null}
          {adminCourseStudentsData && !adminCourseStudentsData.totalDocs ? (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                textAlign="center"
              >
                No Students are available.
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </div>
      {/* Modals */}
      {isAddCourseStudentModalOpen ? (
        <AddCourseStudentModal
          open={isAddCourseStudentModalOpen}
          onClose={toggleAddCourseStudentModal}
        />
      ) : null}
      {editCourseStudentModalData &&
      editCourseStudentModalData.open &&
      editCourseStudentModalData.courseStudent ? (
        <AddCourseStudentModal
          mode="edit"
          courseStudent={editCourseStudentModalData.courseStudent}
          open={editCourseStudentModalData.open}
          onClose={closeCourseStudentEditModal}
        />
      ) : null}
    </TabPanel>
  );
};

export default StudentsTab;
